/* eslint-disable @typescript-eslint/no-explicit-any */
import { DatePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { REGEX_DATE_YYYY_MM_DD } from '@constants';
import {
  ShippingAvailability,
  ShippingItemTime,
  ShippingPaginationResult,
} from '@interfaces';
import { OrderService } from '@services';
import { convertDate } from 'src/app/shared/utils/convert-date';

@Component({
  selector: 'unicon-datetime-grid-dialog',
  templateUrl: './datetime-grid-dialog.component.html',
})
export class DatetimeGridDialogComponent implements OnChanges {
  @Input() firstResult?: ShippingPaginationResult;
  @Input() date?: string;
  @Input() orderId?: string;
  @Input() lead?: any;
  @Input() businessLineId?: number;
  @Output() close: EventEmitter<void> = new EventEmitter<void>();

  protected isLoading: boolean = false;
  protected availabilityIsLoading: boolean = true;

  private page: number = 1;
  private pageSize: number = 6;
  protected form!: FormGroup;
  private resultDates?: ShippingItemTime[];
  private options: Intl.DateTimeFormatOptions = { weekday: 'long' };
  private formatDay: Intl.DateTimeFormat = new Intl.DateTimeFormat(
    'es-ES',
    this.options,
  );
  protected arr = Array(6);
  protected selectedTime: string = '';
  protected placeholderClock = 'Rango de hora';

  constructor(
    private orderService: OrderService,
    private formBuilder: FormBuilder,
  ) {
    this.setForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['firstResult']) {
      const currentValue = changes['firstResult'].currentValue;

      if (
        currentValue &&
        currentValue.results &&
        currentValue.results.length > 0
      ) {
        // Si hay resultados, mostrar datos y desactivar skeleton
        this.availabilityIsLoading = false;
        this.firstResult = currentValue;
      }
    }
  }

  ngOnInit() {
    if (
      this.lead &&
      this.lead?.shipping &&
      this.lead?.shipping?.startDateTime
    ) {
      this.selectedTime = this.formatTimeRange(
        this.lead.shipping.startDateTime,
        this.lead.shipping.endDateTime,
      );
    }

    // Si ya tiene firstResult con datos, desactivar loading
    if (
      this.firstResult &&
      this.firstResult.results &&
      this.firstResult.results.length > 0
    ) {
      this.availabilityIsLoading = false;
    }
    // Si no hay firstResult, mantener availabilityIsLoading = true
    // El padre manejará la petición y enviará los datos vía ngOnChanges
  }

  private setForm(): void {
    this.form = this.formBuilder.group({
      startDateTime: new FormControl<string>(
        '',
        Validators.compose([Validators.required]),
      ),
      endDateTime: new FormControl<string>(
        '',
        Validators.compose([Validators.required]),
      ),
    });
  }

  private getOrderAvailability(): void {
    this.availabilityIsLoading = true;
    if (this.orderId) {
      // SOLUCIÓN MOMENTANEA, FALTA REFACTOR PARA LLEVAR A UTILITARIO Y USAR EN AMBOS COMPONENTES (Track-lead-dialog)
      const dateFormat: string = REGEX_DATE_YYYY_MM_DD.test(this.date as string)
        ? (this.date as string)
        : convertDate(this.date as string);
      this.orderService
        .shippingAvailability(
          this.orderId,
          dateFormat,
          this.page,
          this.pageSize,
        )
        .subscribe({
          next: (data: ShippingPaginationResult) => {
            this.resultDates = (data?.results || []).map(obj => {
              const newArray = obj.availability.map(item => {
                const dateObj = new Date(item.startDateTime);
                const dayName = this.formatDay.format(dateObj);
                return { ...item, dayName };
              });
              return { ...obj, availability: newArray };
            });
            this.firstResult = { ...data, results: this.resultDates };
            this.page = data.page;
            this.pageSize = data.pageSize;
            this.availabilityIsLoading = false;
          },
          error: () => {
            this.isLoading = false;
            this.availabilityIsLoading = false;
          },
        });
    }
  }

  protected onPreviousPage(): void {
    this.page = this.page - 1;
    this.getOrderAvailability();
  }

  public onNextPage(): void {
    this.page = this.page + 1;
    this.getOrderAvailability();
  }

  protected formatTimeRange(startDate: any, endDate: any) {
    const formatTime = (dateString: any) => {
      const date = new Date(dateString);
      let hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const period = hours >= 12 ? 'pm' : 'am';

      // Convertir formato 24h a 12h
      hours = hours % 12 || 12;

      return `${hours}:${minutes} ${period}`;
    };

    return `${formatTime(startDate)} - ${formatTime(endDate)}`;
  }

  protected onSelectedTime(
    _event: MouseEvent,
    time: ShippingAvailability,
  ): void {
    if (time.isAvailable && !this.isLoading) {
      this.firstResult?.results.forEach((times: ShippingItemTime) => {
        times.availability.forEach((availableTime: ShippingAvailability) => {
          if (availableTime) {
            delete availableTime.isSelected;
          }
        });
      });
      const formattedDate = new DatePipe('en-US').transform(
        time.startDateTime,
        'dd/MM/yy',
      );
      time.isSelected = true;
      this.selectedTime = this.formatTimeRange(
        time.startDateTime,
        time.endDateTime,
      );
      this.date = `${time.dayName?.charAt(0).toUpperCase()}${time.dayName?.slice(1)}, ${formattedDate}`;
      this.form.controls['startDateTime'].setValue(time.startDateTime);
      this.form.controls['endDateTime'].setValue(time.endDateTime);
    }
  }

  protected onConfirm(): void {
    this.isLoading = true;
    this.orderService
      .updateShippingDates(this.orderId as string, {
        businessLineId: this.businessLineId!,
        shippingStartDateTime: this.form.value.startDateTime,
        shippingEndDateTime: this.form.value.endDateTime,
      })
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.close.emit();
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  protected convertDateSelected(time: any) {
    const formattedDate = new DatePipe('en-US').transform(time, 'dd/MM/yy');
    const date = new Date(time);
    const dayName = new Intl.DateTimeFormat('es-ES', {
      weekday: 'long',
    }).format(date);
    return `${dayName?.charAt(0).toUpperCase()}${dayName?.slice(1)}, ${formattedDate}`;
  }

  protected onSelectedDate(value: any) {
    const date = value.slice(0, -1);
    this.date = this.convertDateSelected(date);
  }
}
